import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import {
  Grid,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  Collapse,
  Divider,
  Box,
  Toolbar,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@/providers';
import _ from 'lodash';

import RequiredFields from './ResourcesFields';
import EnvironmentFields from './EnvironmentFields/index';
import DataFields from './DataFields';
import ModelFields from './ModelFields';
import WorkerFields from './WorkerFields/index';
import EndpointFields from './EndpointFields';
import JobFormReview from './JobFormReview';
import JOB_TYPES from '@/components/jobs/types';
import toTitleCase from '@/util/toTitleCase';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import { fetchCredentials } from '@/actions/projectCredentials';
import { submitJob, updateJob, fetchJob } from '@/actions/jobs';
import { fetchDatasets } from '@/actions/datasets';
import { fetchCheckpoints } from '@/actions/checkpoints';
import { fetchModels } from '@/actions/models';
import { fetchPublicDatasets } from '@/actions/publicDatasets';
import { fetchPublicCheckpoints } from '@/actions/publicCheckpoints';
import { fetchEnvironments } from '@/actions/environments';
import { fetchProjectDatastores } from '@/actions/projectDatastores';
import { fetchProjectServices } from '@/actions/projectServices';
import { fetchProjectDataConnectors } from '@/actions/projectDataConnectors';
import { clearErrorMessage } from '@/actions/errorMessage';
import { createModel } from '@/actions/models';
import { createDataset } from '@/actions/datasets';
import { createCheckpoint } from '@/actions/checkpoints';
import round from '@/util/round';

const cpu_gpu_type_id = '78201a90-ca48-4147-b6f5-fec51b4bd7db';

const defaultJobRequest = {
  resources: {
    job_name: '',
    gpu_types: [],
    gpu_count: 1,
    cpu_count: 4,
    disk_size: 10,
    max_price: 10,
    preemptible: false,
  },
  environment: {},
  data: {
    input_uri: '',
    output_uri: '',
    datasets: [],
    volumes: [],
    data_connectors: [],
    private_services: [],
  },
  model: {},
  endpoint: {},
  workers: [],
};

export default function JobForm() {
  const { type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useUser();
  const gpu_classes = useSelector((state) => state.gpuClasses || []);
  const api_error = useSelector((state) => state.errorMessage);
  const jobs = useSelector((state) => state.jobs || []);

  const job_type = type || 'notebook';

  // Read search parameters
  const sp = new URLSearchParams(location.search);
  const job_uuid = sp.get('id');
  const source_job_uuid = sp.get('source_job_uuid');
  const isEditMode = !!job_uuid;
  const isCopyMode = !!source_job_uuid;
  const isRerunMode = isCopyMode && job_type !== JOB_TYPES.NOTEBOOK.value;
  const isLlmCopyMode = isCopyMode && job_type === JOB_TYPES.LLM.value;
  const isGuardrailCopyMode =
    isCopyMode && job_type === JOB_TYPES.GUARDRAIL.value;

  // Get source job for copy/edit mode
  const sourceJob =
    (job_uuid && jobs.find((job) => job.job_uuid === job_uuid)) ||
    (source_job_uuid && jobs.find((job) => job.job_uuid === source_job_uuid));

  const [loading, setLoading] = useState(isEditMode || isCopyMode);
  const [reviewing, setReviewing] = useState(false);
  const [jobRequest, setJobRequest] = useState(defaultJobRequest);
  const [submitting, setSubmitting] = useState(false);
  const [credits_per_hour_min, setCreditsPerHourMin] = useState(0);
  const [credits_per_hour_max, setCreditsPerHourMax] = useState(0);
  const [max_price_too_low, setMaxPriceTooLow] = useState(false);

  // Copy mode state
  const [copy_type, setCopyType] = useState(
    isLlmCopyMode || isGuardrailCopyMode || isRerunMode ? 'config' : 'data',
  );
  const [trainml_type, setTrainmlType] = useState('model');
  const [save_model, setSaveModel] = useState('model');
  const [name, setName] = useState('');
  const [name_error, setNameError] = useState(false);

  const [pristine, setPristine] = useState(true);
  const [form_error, setFormError] = useState(true);
  const [resources, setResources] = useState(defaultJobRequest.resources);
  const [resources_error, setResourcesError] = useState(false);
  const [environment, setEnvironment] = useState(defaultJobRequest.environment);
  const [environment_error, setEnvironmentError] = useState(false);
  const [data, setData] = useState(defaultJobRequest.data);
  const [data_error, setDataError] = useState(false);
  const [model, setModel] = useState(defaultJobRequest.model);
  const [model_error, setModelError] = useState(false);
  const [endpoint, setEndpoint] = useState(defaultJobRequest.endpoint);
  const [endpoint_error, setEndpointError] = useState(false);
  const [workers, setWorkers] = useState(defaultJobRequest.workers || []);
  const [workers_error, setWorkersError] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    resources: true,
    data: true,
    model: true,
    workers: true,
    endpoint: true,
    environment: true,
  });

  // Original job data for edit mode comparison
  const [originalJob, setOriginalJob] = useState(null);

  // Fetch job data when in edit or copy mode
  useEffect(() => {
    const fetchJobData = async () => {
      if ((isEditMode || isCopyMode) && !sourceJob) {
        const uuid = job_uuid || source_job_uuid;
        if (uuid) {
          try {
            if (user?.selected_project) {
              await dispatch(fetchJob(user.selected_project, uuid));
            }
          } catch (error) {
            console.error('Error fetching job:', error);
            setLoading(false);
          }
        }
      }
    };

    fetchJobData();
  }, [
    dispatch,
    isEditMode,
    isCopyMode,
    job_uuid,
    source_job_uuid,
    sourceJob,
    user?.selected_project,
  ]);

  // Initialize form with job data
  useEffect(() => {
    if (sourceJob) {
      setOriginalJob(sourceJob);
      if (isEditMode) {
        // Edit mode: initialize with job data
        setResources({
          ...sourceJob.resources,
          job_name: sourceJob.name,
        });
        setData(sourceJob.data || defaultJobRequest.data);
        setModel(sourceJob.model || defaultJobRequest.model);
        setEnvironment(sourceJob.environment || defaultJobRequest.environment);
        setEndpoint(sourceJob.endpoint || defaultJobRequest.endpoint);
        setWorkers(
          sourceJob.workers
            ? sourceJob.workers
                .filter((worker) => worker != null)
                .map((worker) => ({ command: worker?.command || '' }))
            : defaultJobRequest.workers,
        );
        setName(sourceJob.name);
      } else if (isCopyMode) {
        // Copy mode: initialize with job data
        setResources({
          ...sourceJob.resources,
          job_name: `${sourceJob.name} copy`,
        });
        setData(sourceJob.data || defaultJobRequest.data);
        setModel(sourceJob.model || defaultJobRequest.model);
        setEnvironment(sourceJob.environment || defaultJobRequest.environment);
        setEndpoint(sourceJob.endpoint || defaultJobRequest.endpoint);
        // For convert type, blank out workers (don't copy jupyter lab command)
        if (
          copy_type === 'convert' ||
          job_type === JOB_TYPES.LLM.value ||
          job_type === JOB_TYPES.GUARDRAIL.value
        ) {
          setWorkers(defaultJobRequest.workers);
        } else {
          setWorkers(
            sourceJob.workers
              ? sourceJob.workers
                  .filter((worker) => worker != null)
                  .map((worker) => ({ command: worker?.command || '' }))
              : defaultJobRequest.workers,
          );
        }
        setName(`${sourceJob.name} copy`);
      }
      setLoading(false);
    }
  }, [sourceJob, isEditMode, isCopyMode, copy_type]);

  useEffect(() => {
    window.gtag('event', 'job_form_open', {
      customer_uuid: user?.id,
      job_type: job_type,
    });
    dispatch(fetchPublicDatasets());
    dispatch(fetchPublicCheckpoints());
    if (user?.selected_project) {
      dispatch(fetchCredentials(user.selected_project));
      dispatch(fetchDatasets(user.selected_project));
      dispatch(fetchCheckpoints(user.selected_project));
      dispatch(fetchModels(user.selected_project));
      dispatch(fetchProjectDatastores(user.selected_project));
      dispatch(fetchProjectServices(user.selected_project));
      dispatch(fetchProjectDataConnectors(user.selected_project));
    }
    dispatch(fetchEnvironments());
    // fetchUser is a plain async function, not a thunk - Layout handles user fetching
  }, [dispatch, user?.id, user?.selected_project, job_type]);

  useEffect(() => {
    return () => {
      dispatch(clearErrorMessage());
    };
  }, [dispatch]);

  const handleChange = useCallback((changeType, values, error) => {
    setPristine(false);

    switch (changeType) {
      case 'resources':
        setResources(values);
        setResourcesError(error);
        if (values.job_name) {
          setName(values.job_name);
        }
        break;
      case 'environment':
        setEnvironment(values);
        setEnvironmentError(error);
        break;
      case 'data':
        setData(values);
        setDataError(error);
        break;
      case 'model':
        setModel(values);
        setModelError(error);
        break;
      case 'workers':
        setWorkers(values);
        setWorkersError(error);
        break;
      case 'endpoint':
        setEndpoint(values);
        setEndpointError(error);
        break;
      default:
        break;
    }
  }, []);

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    setNameError(value === '' || /[#$<>+%!`&*'"|{}?/\\:=@]/.test(value));
    setPristine(false);
  };

  const handleCopyTypeChange = (e) => {
    const new_copy_type = e.target.value;
    setCopyType(new_copy_type);
    setPristine(false);
    // Blank out workers when converting (don't copy jupyter lab command)
    if (new_copy_type === 'convert') {
      setWorkers(defaultJobRequest.workers);
    }
  };

  useEffect(() => {
    const form_error_calculated =
      resources_error ||
      environment_error ||
      data_error ||
      model_error ||
      workers_error ||
      ([
        JOB_TYPES.ENDPOINT.value,
        JOB_TYPES.LLM.value,
        JOB_TYPES.GUARDRAIL.value,
      ].includes(job_type) &&
        endpoint_error) ||
      (copy_type === 'evefs' && name_error);
    setFormError(form_error_calculated);
  }, [
    resources_error,
    environment_error,
    data_error,
    model_error,
    workers_error,
    endpoint_error,
    job_type,
    copy_type,
    name_error,
  ]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Edit mode: update job directly
    if (isEditMode) {
      setSubmitting(true);
      try {
        let update = {};

        if (resources.gpu_type_id !== originalJob.resources.gpu_type_id) {
          update.resources = update.resources || {};
          update.resources.gpu_type_id = resources.gpu_type_id;
        }
        if (!_.isEqual(resources.gpu_types, originalJob.resources.gpu_types)) {
          update.resources = update.resources || {};
          update.resources.gpu_types = resources.gpu_types;
        }
        if (resources.gpu_count !== originalJob.resources.gpu_count) {
          update.resources = update.resources || {};
          update.resources.gpu_count = resources.gpu_count;
        }
        if (resources.cpu_count !== originalJob.resources.cpu_count) {
          update.resources = update.resources || {};
          update.resources.cpu_count = resources.cpu_count;
        }

        if (!_.isEqual(data, originalJob.data)) {
          update.data = {};
          if (!_.isEqual(data.datasets, originalJob.data.datasets)) {
            update.data.datasets = data.datasets ? [...data.datasets] : [];
          }
          if (data.output_type !== originalJob.data.output_type) {
            if (!data.output_type || data.output_type === '') {
              update.data.output_type = null;
              update.data.output_uri = null;
              update.data.output_options = null;
            } else {
              update.data.output_type = data.output_type;
              update.data.output_uri = data.output_uri;
              update.data.output_options = data.output_options
                ? { ...data.output_options }
                : {};
            }
          } else {
            update.data.output_type = data.output_type;
            if (data.output_uri !== originalJob.data.output_uri) {
              update.data.output_uri = data.output_uri;
            }
            if (
              !_.isEqual(data.output_options, originalJob.data.output_options)
            ) {
              update.data.output_options = data.output_options
                ? { ...data.output_options }
                : {};
            }
          }
        }
        if (!_.isEqual(model, originalJob.model)) {
          update.model = {};
          if (!_.isEqual(model.checkpoints, originalJob.model.checkpoints)) {
            update.model.checkpoints = model.checkpoints
              ? [...model.checkpoints]
              : [];
          }
        }

        if (user?.selected_project) {
          await dispatch(updateJob(user.selected_project, job_uuid, update));
        }
        navigate(`/jobs/${job_type}`);
      } catch (error) {
        setSubmitting(false);
        throw error;
      }
      return;
    }

    // Copy mode: handle different copy types
    if (isCopyMode) {
      // evefs type goes directly to submission (creates model/dataset/checkpoint)
      if (copy_type === 'evefs') {
        setSubmitting(true);
        try {
          const payload = {
            name: name,
            source_type: 'trainml',
            source_uri: source_job_uuid,
            source_options: {
              type: 'job',
              save_model: save_model === 'model',
            },
          };
          switch (trainml_type) {
            case 'dataset':
              if (user?.selected_project) {
                await dispatch(createDataset(user.selected_project, payload));
              }
              navigate('/dataset');
              break;
            case 'checkpoint':
              await dispatch(createCheckpoint(user.selected_project, payload));
              navigate('/checkpoint');
              break;
            case 'model':
              if (user?.selected_project) {
                await dispatch(createModel(user.selected_project, payload));
              }
              navigate('/model');
              break;
            default:
              throw Error('Unknown trainml type');
          }
        } catch (error) {
          setSubmitting(false);
          throw error;
        }
        return;
      }

      // Other copy types go to review step
      const newJobRequest = {
        name: name || resources.job_name,
        resources: { ...resources },
        environment: { ...environment },
        data: { ...data },
        model: { ...model },
        endpoint: { ...endpoint },
        workers: [...workers],
        type: copy_type === 'convert' ? JOB_TYPES.TRAINING.value : job_type,
      };

      // Add source_job_uuid for full data copy (never for LLM/guardrail — config-only copy)
      if (
        !isLlmCopyMode &&
        !isGuardrailCopyMode &&
        (copy_type === 'data' || copy_type === 'convert')
      ) {
        newJobRequest.source_job_uuid = source_job_uuid;
      }

      let min_price = 10000,
        max_price = 0,
        use_price = 10000;
      newJobRequest.resources.gpu_types?.forEach((gpu_type_id) => {
        const selected_gpu_class = gpu_classes.find(
          (gpu_class) => gpu_class.id === gpu_type_id,
        );
        if (selected_gpu_class) {
          min_price = Math.min(selected_gpu_class.price.min, min_price);
          max_price = Math.max(selected_gpu_class.price.max, max_price);
          use_price = Math.min(selected_gpu_class.price.use, use_price);
        }
      });

      const cpu_only =
        newJobRequest.resources.gpu_types?.[0] === cpu_gpu_type_id;

      const credits_per_hour_min_calc = round(
        min_price *
          (cpu_only
            ? newJobRequest.resources.cpu_count
            : newJobRequest.resources.gpu_count) *
          (newJobRequest.workers.length || 1),
        4,
      );
      const credits_per_hour_max_calc = round(
        max_price *
          (cpu_only
            ? newJobRequest.resources.cpu_count
            : newJobRequest.resources.gpu_count) *
          (newJobRequest.workers.length || 1),
        4,
      );
      const max_price_too_low_calc =
        newJobRequest.resources.max_price < use_price;

      // fetchUser is a plain async function, not a thunk - Layout handles user fetching

      setCreditsPerHourMin(credits_per_hour_min_calc);
      setCreditsPerHourMax(credits_per_hour_max_calc);
      setMaxPriceTooLow(max_price_too_low_calc);
      setJobRequest(newJobRequest);
      setReviewing(true);
      return;
    }

    // New mode: go to review step
    const newJobRequest = {
      name: resources.job_name,
      resources: { ...resources },
      environment: { ...environment },
      data: { ...data },
      model: { ...model },
      endpoint: { ...endpoint },
      workers: [...workers],
      type: job_type,
    };

    let min_price = 10000,
      max_price = 0,
      use_price = 10000;
    newJobRequest.resources.gpu_types?.forEach((gpu_type_id) => {
      const selected_gpu_class = gpu_classes.find(
        (gpu_class) => gpu_class.id === gpu_type_id,
      );
      if (selected_gpu_class) {
        min_price = Math.min(selected_gpu_class.price.min, min_price);
        max_price = Math.max(selected_gpu_class.price.max, max_price);
        use_price = Math.min(selected_gpu_class.price.use, use_price);
      }
    });

    const cpu_only = newJobRequest.resources.gpu_types?.[0] === cpu_gpu_type_id;

    const credits_per_hour_min_calc = round(
      min_price *
        (cpu_only
          ? newJobRequest.resources.cpu_count
          : newJobRequest.resources.gpu_count) *
        (newJobRequest.workers.length || 1),
      4,
    );
    const credits_per_hour_max_calc = round(
      max_price *
        (cpu_only
          ? newJobRequest.resources.cpu_count
          : newJobRequest.resources.gpu_count) *
        (newJobRequest.workers.length || 1),
      4,
    );
    const max_price_too_low_calc =
      newJobRequest.resources.max_price < use_price;

    // fetchUser is a plain async function, not a thunk - Layout handles user fetching

    setCreditsPerHourMin(credits_per_hour_min_calc);
    setCreditsPerHourMax(credits_per_hour_max_calc);
    setMaxPriceTooLow(max_price_too_low_calc);
    setJobRequest(newJobRequest);
    setReviewing(true);
  };

  const handleReviewCancel = () => {
    setReviewing(false);
  };

  const handleBack = () => {
    navigate(`/jobs/${job_type}`);
  };

  const handleReviewSubmit = async () => {
    setSubmitting(true);
    window.gtag('event', 'job_submitted', {
      customer_uuid: user?.id,
      job_type: jobRequest.type,
    });
    try {
      if (user?.selected_project) {
        const submit_payload = { ...jobRequest };
        if (
          submit_payload.type === JOB_TYPES.LLM.value ||
          submit_payload.type === JOB_TYPES.GUARDRAIL.value
        ) {
          // Keep review UI happy, but omit unused sections in the create payload.
          delete submit_payload.data;
          delete submit_payload.environment;
          delete submit_payload.workers;
          delete submit_payload.source_job_uuid;
          if (
            submit_payload.type === JOB_TYPES.GUARDRAIL.value ||
            !submit_payload.model?.checkpoints?.length
          ) {
            delete submit_payload.model;
          }
        }
        const endpoint_job_types = [
          JOB_TYPES.ENDPOINT.value,
          JOB_TYPES.LLM.value,
          JOB_TYPES.GUARDRAIL.value,
        ];
        if (!endpoint_job_types.includes(submit_payload.type)) {
          delete submit_payload.endpoint;
        }
        await dispatch(submitJob(user.selected_project, submit_payload));
      }
      window.gtag('event', 'job_created', {
        customer_uuid: user?.id,
        job_type: jobRequest.type,
      });
      navigate(`/jobs/${jobRequest.type}`);
    } catch (error) {
      setSubmitting(false);
      window.gtag('event', 'job_create_failed', {
        customer_uuid: user?.id,
        job_type: jobRequest.type,
      });
      throw error;
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const renderSection = (sectionKey, title, children, showDivider = true) => {
    return (
      <Grid container spacing={2}>
        <Grid size={12}>
          <Grid
            container
            alignItems='center'
            sx={{ cursor: 'pointer' }}
            onClick={() => toggleSection(sectionKey)}
          >
            <Grid size='grow'>
              <IconButton size='small'>
                {expandedSections[sectionKey] ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Grid>
            <Grid size='grow'>
              <Typography variant='h6'>{title}</Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={12}>
          <Collapse in={expandedSections[sectionKey]}>{children}</Collapse>
        </Grid>
        {showDivider && (
          <Grid size={12}>
            <Divider />
          </Grid>
        )}
      </Grid>
    );
  };

  if (loading) {
    return (
      <Container>
        <Paper sx={{ p: 3 }}>
          <Typography>Loading...</Typography>
        </Paper>
      </Container>
    );
  }

  if (reviewing && !isEditMode && !(isCopyMode && copy_type === 'evefs')) {
    return (
      <JobFormReview
        jobRequest={jobRequest}
        submitting={submitting}
        credits_per_hour_min={credits_per_hour_min}
        credits_per_hour_max={credits_per_hour_max}
        max_price_too_low={max_price_too_low}
        onSubmit={handleReviewSubmit}
        onCancel={handleReviewCancel}
      />
    );
  }

  // Determine title
  let title = `New ${toTitleCase(job_type)} Job`;
  if (isEditMode) {
    title = `Edit ${toTitleCase(job_type)} Job`;
  } else if (isCopyMode && sourceJob) {
    title = `Copy ${sourceJob.name}`;
  }

  const showWorkers =
    [JOB_TYPES.TRAINING.value, JOB_TYPES.INFERENCE.value].includes(job_type) ||
    (isCopyMode && copy_type === 'convert');
  const showEndpoint = [
    JOB_TYPES.ENDPOINT.value,
    JOB_TYPES.LLM.value,
    JOB_TYPES.GUARDRAIL.value,
  ].includes(job_type);
  const hideDataModelEnv = [
    JOB_TYPES.LLM.value,
    JOB_TYPES.GUARDRAIL.value,
  ].includes(job_type);

  // Show trainml fields only when copy_type is 'evefs'
  const showTrainmlFields = isCopyMode && copy_type === 'evefs';

  return (
    <Container>
      <Paper sx={{ display: 'flex', width: '100%' }} elevation={2}>
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          <Grid size={12}>
            <Paper sx={{ display: 'flex', width: '100%' }} elevation={2}>
              <Toolbar>
                <Stack spacing={2} direction='row' alignItems='center'>
                  <IconButton onClick={handleBack} disabled={submitting}>
                    <ArrowBack />
                  </IconButton>
                  <Typography variant='h5'>{title}</Typography>
                </Stack>
              </Toolbar>
            </Paper>
          </Grid>

          <Grid size={12}>
            <Box padding='15px'>
              {isCopyMode && job_type === JOB_TYPES.NOTEBOOK.value && (
                <Grid container spacing={2} sx={{ mb: 2, flexGrow: 1 }}>
                  <Grid size={12}>
                    <FormControl component='fieldset'>
                      <FormLabel component='legend'>Copy Type</FormLabel>
                      <RadioGroup
                        value={copy_type}
                        onChange={handleCopyTypeChange}
                        row
                      >
                        <FormControlLabel
                          value='data'
                          control={<Radio />}
                          label='Full (Data and Configuration)'
                        />
                        <FormControlLabel
                          value='config'
                          control={<Radio />}
                          label='Configuration Only'
                        />
                        <FormControlLabel
                          value='convert'
                          control={<Radio />}
                          label='Convert To Training Job'
                        />
                        <FormControlLabel
                          value='evefs'
                          control={<Radio />}
                          label='Save to EverywhereFS'
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              {showTrainmlFields ? (
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <TextField
                      name='name'
                      label={`New ${toTitleCase(trainml_type)} Name`}
                      value={name}
                      required
                      fullWidth
                      onChange={handleNameChange}
                      error={name_error}
                      helperText={
                        name_error
                          ? `${
                              copy_type === 'config' ? 'Job' : 'Model'
                            } names cannot be blank and cannot include the following characters: #$<>+%!\`&*'"|{}?/\\:=@`
                          : ''
                      }
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        name='trainml_type'
                        value={trainml_type}
                        onChange={(e) => {
                          setTrainmlType(e.target.value);
                          setPristine(false);
                        }}
                        label='Type'
                      >
                        <MenuItem value='checkpoint'>Checkpoint</MenuItem>
                        <MenuItem value='dataset'>Dataset</MenuItem>
                        <MenuItem value='model'>Model</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={6}>
                    <FormControl fullWidth>
                      <InputLabel>Save Directory</InputLabel>
                      <Select
                        name='save_model'
                        value={save_model}
                        onChange={(e) => {
                          setSaveModel(e.target.value);
                          setPristine(false);
                        }}
                        label='Save Directory'
                      >
                        <MenuItem value='model'>Models</MenuItem>
                        <MenuItem value='output'>Output</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid size={12}>
                    {renderSection(
                      'resources',
                      'Resources',
                      <RequiredFields
                        initialValues={resources}
                        type={job_type}
                        onChange={handleChange}
                        edit={isEditMode}
                      />,
                    )}
                  </Grid>

                  <Grid size={12}>
                    {!hideDataModelEnv
                      ? renderSection(
                          'data',
                          'Data',
                          <DataFields
                            initialValues={data}
                            type={
                              isCopyMode && copy_type === 'convert'
                                ? JOB_TYPES.TRAINING.value
                                : job_type
                            }
                            onChange={handleChange}
                            edit={isEditMode}
                          />,
                        )
                      : null}
                  </Grid>

                  <Grid size={12}>
                    {!hideDataModelEnv
                      ? renderSection(
                          'model',
                          'Model',
                          <ModelFields
                            initialValues={model}
                            type={
                              isCopyMode && copy_type === 'convert'
                                ? JOB_TYPES.TRAINING.value
                                : job_type
                            }
                            onChange={handleChange}
                            edit={isEditMode}
                            copy={
                              isCopyMode &&
                              copy_type !== 'config' &&
                              !isLlmCopyMode &&
                              !isGuardrailCopyMode
                            }
                          />,
                        )
                      : null}
                  </Grid>

                  {showWorkers && (
                    <Grid size={12}>
                      {renderSection(
                        'workers',
                        'Workers',
                        <WorkerFields
                          initialValues={workers}
                          type={
                            isCopyMode && copy_type === 'convert'
                              ? JOB_TYPES.TRAINING.value
                              : job_type
                          }
                          onChange={handleChange}
                          edit={isEditMode}
                        />,
                      )}
                    </Grid>
                  )}

                  {showEndpoint && (
                    <Grid size={12}>
                      {renderSection(
                        'endpoint',
                        'Endpoint',
                        <EndpointFields
                          initialValues={endpoint}
                          initialModel={model}
                          type={job_type}
                          onChange={handleChange}
                          edit={isEditMode}
                        />,
                      )}
                    </Grid>
                  )}

                  <Grid size={12}>
                    {!hideDataModelEnv
                      ? renderSection(
                          'environment',
                          'Environment',
                          <EnvironmentFields
                            initialValues={environment}
                            type={
                              isCopyMode && copy_type === 'convert'
                                ? JOB_TYPES.TRAINING.value
                                : job_type
                            }
                            onChange={handleChange}
                            edit={isEditMode}
                          />,
                          false,
                        )
                      : null}
                  </Grid>
                </Grid>
              )}
            </Box>
          </Grid>

          <Grid size={12}>
            <Divider />
            <Toolbar>
              <Stack spacing={2} direction='row'>
                <Button
                  variant='outlined'
                  startIcon={<ArrowBack />}
                  onClick={handleBack}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  disabled={
                    pristine ||
                    form_error ||
                    (showTrainmlFields && (name_error || !name)) ||
                    submitting
                  }
                  endIcon={isEditMode ? undefined : <ArrowForward />}
                  startIcon={
                    submitting ? <CircularProgress size={20} /> : undefined
                  }
                  onClick={handleFormSubmit}
                >
                  {isEditMode ? 'Update' : 'Next'}
                </Button>
              </Stack>
            </Toolbar>
          </Grid>
        </Grid>
      </Paper>
      <ErrorSnackBar message={api_error} />
    </Container>
  );
}
